/**
 * Aiming & Firing Controls — turret angle, charge oscillation, fire trigger.
 *
 * UP/DOWN (or W/S): Rotate aim angle
 * Hold trigger (ACTION.CHARGE): Oscillating power charge (0.1 ↔ 1.0)
 * Release trigger (ACTION.FIRE): Fire at charged power (must charge first)
 * Cancel (ACTION.CANCEL): Abort charge without firing
 *
 * Movement extracted to movement.ts.
 */

import { PHYSICS, AIM, AIM_FX, GRAPPLE, ACTION, SLINGSHOT } from '../constants';
import type { GameState, Player } from '../types';
import type { InputProvider } from '../input';

import { Keys, Input } from 'bonkjs';
import { getDefaultWeapon, createProjectile } from '../weapons';
import { mirrorAirborne, mirrorSettle } from '../physics/transitions';
import { getTankShot, getEffectiveShots, lookupTank } from '../tanks';
import type { ShotTierDef } from '../tanks';
import { modeUsesShotTiers, modeIsAscent } from './mode';
import { justPressed } from './input-edge';
import { isChargeReady, drainCharge } from './weapon-charge';

/** Resolve charge config: per-shot overrides → tank-level fallback → defaults. */
function getChargeConfig(player: Player, state: GameState): { instantFire: boolean; chargeMode: 'oscillating' | 'linear'; chargeRate: number } {
  const tankDef = lookupTank(player.tankId);
  if (modeUsesShotTiers(state.modeState.mode)) {
    const shotDef: ShotTierDef = getTankShot(player.tankId, player.selectedShotTier, player.terraformerMode);
    return {
      instantFire: shotDef.instantFire ?? tankDef.instantFire ?? false,
      chargeMode: shotDef.chargeMode ?? 'oscillating',
      chargeRate: shotDef.chargeRate ?? PHYSICS.CHARGE_RATE,
    };
  }
  return {
    instantFire: tankDef.instantFire ?? false,
    chargeMode: 'oscillating',
    chargeRate: PHYSICS.CHARGE_RATE,
  };
}

export interface AimingResult {
  fired: boolean;
  aimReversed: boolean;
  grappleEvent: 'launch' | null;
  turnConsumed: boolean;   // true when arming a requiresArming shot (end turn, no fire)
  disarmed: boolean;       // true when player disarmed by selecting a different tier
}

export function updateAiming(state: GameState, input: InputProvider): AimingResult {
  const player = state.players[state.currentPlayer];

  // During ragdoll (e.g. post-burst), block all input until ragdoll expires.
  // Turn timer still ticks — ragdoll is part of the turn budget.
  if (player.tumble) {
    return { fired: false, aimReversed: false, grappleEvent: null, turnConsumed: false, disarmed: false };
  }

  let aimReversed = false;
  let grappleEvent: 'launch' | null = null;

  // --- Shot tier selection (1/2/3) — only in non-lottery modes ---
  // Terraformer: Digit3 is mode switch (handled in phases.ts), skip here
  let turnConsumed = false;
  let disarmed = false;
  const grappleActive = state.grapple.active !== null;
  if (modeUsesShotTiers(state.modeState.mode) && !grappleActive) {
    const tierKeys = [Keys.Digit1, Keys.Digit2, Keys.Digit3, Keys.Digit4, Keys.Digit5] as const;
    const shots = getEffectiveShots(player.tankId, player.terraformerMode);
    for (let i = 0; i < Math.min(shots.length, tierKeys.length); i++) {
      if (i === 2 && player.tankId === 'terraformer') continue; // slot 3 = mode switch
      if (justPressed(tierKeys[i], input)) {
        const tier = i + 1;
        const shotDef = getTankShot(player.tankId, tier, player.terraformerMode);

        // --- Disarm: while armed, selecting any OTHER tier disarms (no refund) ---
        if (player.armedShotTier !== null && tier !== player.armedShotTier) {
          player.armedShotTier = null;
          disarmed = true;
          // Fall through to normal tier selection
        }

        if (tier === 1 || isChargeReady(player)) {
          // --- Arming: requiresArming shot → set armed, end turn ---
          if (shotDef.requiresArming && player.armedShotTier === null) {
            player.armedShotTier = tier;
            player.selectedShotTier = tier;
            player.selectedWeapon = shotDef.weapon;
            player.charging = false;
            player.power = 0;
            player.chargeGraceTimer = 0;
            turnConsumed = true;
            break; // arming ends the tier selection loop
          }
          player.selectedShotTier = tier;
          player.selectedWeapon = shotDef.weapon;
        }
      }
    }
  }

  // --- Swap button (cycle tiers) ---
  if (modeUsesShotTiers(state.modeState.mode) && !grappleActive && !turnConsumed) {
    const swapDown = input.getKey(ACTION.SWAP);
    if (swapDown && !player.swapHeld) {
      // While armed, swap disarms first
      if (player.armedShotTier !== null) {
        player.armedShotTier = null;
        disarmed = true;
      }
      const shots = getEffectiveShots(player.tankId, player.terraformerMode);
      const maxSlot = player.tankId === 'terraformer' ? 2 : shots.length;
      for (let i = 1; i <= maxSlot; i++) {
        const idx = (player.selectedShotTier - 1 + i) % maxSlot;
        const tier = idx + 1;
        const shotDef = getTankShot(player.tankId, tier, player.terraformerMode);
        // Skip arming tiers during swap cycle (prevent accidental arm)
        if (shotDef.requiresArming) continue;
        if (tier === 1 || isChargeReady(player)) {
          player.selectedShotTier = tier;
          player.selectedWeapon = shotDef.weapon;
          break;
        }
      }
    }
    player.swapHeld = swapDown;
  }

  // Block input during portal jump animation
  if (player.portalJumpFrame >= 0) {
    return { fired: false, aimReversed: false, grappleEvent: null, turnConsumed: false, disarmed: false };
  }

  // Grapple input (G key) — disabled in ascent mode, disabled while tuning other systems
  const gKeyHeld = !modeIsAscent(state.modeState.mode) && input.getKey(Keys.G);
  player.grappleKeyHeld = gKeyHeld;

  // --- Block firing during active grapple ---
  const grappleRetracting = grappleActive && state.grapple.active!.phase === 'retracting';

  if (grappleActive) {
    // During traveling: no firing
    // During retracting: air control only (handled in movement.ts gate at call site), no firing
    if (grappleRetracting) {
      // Air control — nudge velocity, no momentum cost
      let airDir = 0;
      if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) airDir -= 1;
      if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) airDir += 1;
      if (airDir !== 0) {
        player.vx += airDir * GRAPPLE.AIR_CONTROL_ACCEL;
        // No speed cap — pull force (updateRetraction) drives speed
        // beyond AIR_CONTROL_MAX_SPEED. Capping here would fight the rope.
      }
    }

    // Still update direction tracking
    player.prevMoveDir = player.moveDir;
    player.moveDir = 0;

    return { fired: false, aimReversed: false, grappleEvent, turnConsumed: false, disarmed: false };
  }

  // --- Aim angle (Arrow UP/DOWN) — accelerating: forgiving start, faster the longer held ---
  // Save angle BEFORE modification — renderer uses (angle - aimPrevAngle) for angular velocity
  const angleBefore = player.angle;
  const aimUp = input.getKey(Keys.ArrowUp);
  const aimDown = input.getKey(Keys.ArrowDown);

  if (aimUp || aimDown) {
    const wasIdle = player.aimHeldFrames === 0;
    player.aimHeldFrames++;
    const t = Math.min(player.aimHeldFrames / AIM.RAMP_FRAMES, 1);
    const smooth = t * t * (3 - 2 * t); // smoothstep — fast out of the sluggish zone
    const speed = AIM.MIN_SPEED + (AIM.MAX_SPEED - AIM.MIN_SPEED) * smooth;
    const aimDir = aimUp ? 1 : -1;
    player.angle += aimDir * speed;

    // --- Aim squash: start-of-aim + reversal ---
    if (wasIdle && player.aimPrevDir === 0) {
      // Starting to aim from idle (2+ gap frames) — subtle weight squash
      const jitter = 1 + (Math.random() * 2 - 1) * AIM_FX.START_SQUASH_JITTER;
      player.squashVel -= AIM_FX.START_SQUASH_BASE * jitter;
    } else if (player.aimPrevDir !== 0 && aimDir !== player.aimPrevDir) {
      // Reversal — velocity-scaled squash (faster sweep = bigger squash)
      const impulse = AIM_FX.REVERSAL_SQUASH_BASE
        + player.aimPrevSpeed * AIM_FX.REVERSAL_SQUASH_SCALE;
      const capped = Math.min(impulse, AIM_FX.REVERSAL_SQUASH_MAX);
      const jitter = 1 + (Math.random() * 2 - 1) * AIM_FX.REVERSAL_SQUASH_JITTER;
      player.squashVel -= capped * jitter;
      aimReversed = true;
    }

    // Track aim direction and speed (persist through gap frames for reversal detection)
    player.aimPrevDir = aimDir;
    player.aimPrevSpeed = Math.abs(speed);
  } else {
    // Gap frame — clear aimPrevDir after 2+ idle frames so next aim counts as "start"
    if (player.aimHeldFrames === 0) {
      player.aimPrevDir = 0; // 2nd consecutive idle frame → fully idle
    }
    player.aimHeldFrames = 0;
  }

  // Wrap angle to full 360° range
  if (player.angle > AIM.MAX_ANGLE) player.angle -= Math.PI * 2;
  if (player.angle < AIM.MIN_ANGLE) player.angle += Math.PI * 2;

  // --- Instant fire (per-shot or tank-level) — skip charge, fire immediately on button press ---
  const chargeConfig = getChargeConfig(player, state);
  if (chargeConfig.instantFire && input.getKey(ACTION.CHARGE) && !player.fireHeld) {
    player.fireHeld = true;
    player.power = 1.0;
    player.charging = false;
    player.chargeGraceTimer = 0;
    player.tumble = false;
    player.hitstunFrames = 0;
    player.launchArmor = 0;
    if (player.settled) mirrorSettle(player); else mirrorAirborne(player);
    fire(state);
    player.aimPrevAngle = angleBefore;
    return { fired: true, aimReversed, grappleEvent, turnConsumed: false, disarmed: false };
  }

  // --- Charge (ACTION.CHARGE): hold to charge, release to cancel ---
  if (input.getKey(ACTION.CHARGE)) {
    if (!player.charging) {
      // Fresh charge start
      player.charging = true;
      player.chargeDir = 1;
      if (Input.inputMode === 'touch') {
        // Slingshot: start ramp at minimum, wobble offset = 0
        player.wobbleOffset = 0;
        player.slingshotRamp = 0.1;
        player.power = 0.1;
      } else {
        // Oscillation/linear: start at min
        player.power = chargeConfig.chargeMode === 'linear' ? 0 : 0.1;
      }
    }

    if (Input.inputMode === 'touch') {
      // Ramp effective center toward touch target (same rate as oscillation charge)
      const target = Math.max(player.slingshotCenter, 0.1);
      if (target > player.slingshotRamp) {
        player.slingshotRamp = Math.min(player.slingshotRamp + SLINGSHOT.RAMP_RATE, target);
      } else if (target < player.slingshotRamp) {
        player.slingshotRamp = Math.max(player.slingshotRamp - SLINGSHOT.RAMP_RATE, target);
      }
      // Wobble: oscillate ±AMPLITUDE around ramped center
      player.wobbleOffset += SLINGSHOT.WOBBLE_RATE * player.chargeDir;
      if (player.wobbleOffset >= SLINGSHOT.WOBBLE_AMPLITUDE) {
        player.wobbleOffset = SLINGSHOT.WOBBLE_AMPLITUDE;
        player.chargeDir = -1;
      } else if (player.wobbleOffset <= -SLINGSHOT.WOBBLE_AMPLITUDE) {
        player.wobbleOffset = -SLINGSHOT.WOBBLE_AMPLITUDE;
        player.chargeDir = 1;
      }
      player.power = Math.max(0.1, Math.min(1.0, player.slingshotRamp + player.wobbleOffset));
    } else if (chargeConfig.chargeMode === 'linear') {
      // Linear: ramp 0→1.0, cap at max (no oscillation)
      player.power = Math.min(player.power + chargeConfig.chargeRate, 1.0);
    } else {
      // Oscillating (default for MnK/gamepad): bounce between 0.1 and 1.0
      player.power += chargeConfig.chargeRate * player.chargeDir;
      if (player.power >= 1.0) { player.power = 1.0; player.chargeDir = -1; }
      else if (player.power <= 0.1) { player.power = 0.1; player.chargeDir = 1; }
    }
    // Clear grace timer while actively charging (fresh charge takes priority)
    player.chargeGraceTimer = 0;
  } else if (player.charging) {
    // Release charge — cache power for grace window so fire can use it
    player.chargeGracePower = player.power;
    player.chargeGraceTimer = 6; // ~100ms at 60fps
    player.charging = false;
    player.power = 0;
  }

  // Tick down grace timer
  if (player.chargeGraceTimer > 0) {
    player.chargeGraceTimer--;
  }

  // --- Fire (ACTION.FIRE): pulse on charge release → fire at charged power ---
  if (input.getKey(ACTION.FIRE) && !player.fireHeld) {
    player.fireHeld = true;
    if (player.charging) {
      // Still charging — fire at current power
    } else if (player.chargeGraceTimer > 0) {
      // Charge just released — use cached power from grace window
      player.power = player.chargeGracePower;
    } else {
      // No charge power — swallow fire, don't shoot
      player.chargeGraceTimer = 0;
    }
    if (player.power > 0) {
      // Verify ult meter before firing (may have changed since tier selection)
      // Skip check when armed — meter was already validated on arm turn
      if (modeUsesShotTiers(state.modeState.mode) && player.armedShotTier === null) {
        const fireShotDef = getTankShot(player.tankId, player.selectedShotTier, player.terraformerMode);
        if (player.selectedShotTier > 1 && !isChargeReady(player)) {
          // Can't afford — auto-downgrade to Shot 1
          player.selectedShotTier = 1;
          player.selectedWeapon = getTankShot(player.tankId, 1, player.terraformerMode).weapon;
        }
      }
      player.charging = false;
      player.chargeGraceTimer = 0;
      player.tumble = false;
      player.hitstunFrames = 0;
      player.launchArmor = 0;
      if (player.settled) mirrorSettle(player); else mirrorAirborne(player);
      fire(state);
      // Clear armed state after firing (momentum already paid on arm turn)
      if (player.armedShotTier !== null) {
        player.armedShotTier = null;
      }
      player.aimPrevAngle = angleBefore;
      return { fired: true, aimReversed, grappleEvent, turnConsumed: false, disarmed: false };
    }
  }
  if (!input.getKey(ACTION.FIRE)) {
    player.fireHeld = false;
  }

  // Store pre-modification angle so renderer can compute angular velocity next frame
  player.aimPrevAngle = angleBefore;

  return { fired: false, aimReversed, grappleEvent, turnConsumed, disarmed };
}

/** Original VA nonlinear power curve — low charge is much weaker than proportional */
export function chargeToPower(linearCharge: number): number {
  return linearCharge * (0.4 + 0.6 * linearCharge);
}

export function fire(state: GameState): void {
  const player = state.players[state.currentPlayer];
  const weapon = player.selectedWeapon ?? getDefaultWeapon();

  // Drain ult meter on S2/S3 fire (skip if armed — drain happens on actual fire, not arm)
  if (modeUsesShotTiers(state.modeState.mode)) {
    if (player.selectedShotTier > 1 || player.armedShotTier !== null) {
      drainCharge(player);
    }
  }

  // Hitscan weapons skip projectile creation — beam is fired in phases.ts
  if (weapon.hitscan) {
    player.power = 0;
    return;
  }

  const effectivePower = chargeToPower(player.power) * PHYSICS.MAX_POWER * weapon.projectileSpeed;
  const vx = Math.cos(player.angle) * effectivePower;
  const vy = -Math.sin(player.angle) * effectivePower; // negative: up in screen coords

  const spawnOffset = PHYSICS.ECB_RADIUS + weapon.projectileRadius + 2;

  const proj = createProjectile(
    weapon,
    player.x + Math.cos(player.angle) * spawnOffset,
    player.y - Math.sin(player.angle) * spawnOffset,
    vx, vy,
    state.currentPlayer,
    state.frameCount,
  );
  // Store charge power for onTick behavior hooks (e.g. Thunderclap dash distance)
  proj.behaviorState.chargePower = player.power;
  state.projectiles.push(proj);

  // Match stat tracking
  if (state.matchStats) {
    state.matchStats.shotsFired[state.currentPlayer]++;
  }

  // Reset power for next turn (power stays at fired value until turn advances)
  player.power = 0;
}
